<template>
  <div class="login-container">
    <!-- 背景图容器 -->
    <div class="login-bg" :class="{ 'bg-zoom': isLogging }"></div>

    <!-- 登录表单，始终居中 -->
    <div class="login-right">
      <div class="login-card" :class="{ 'login-card-animation': true }">
        <div class="login-brand">
          <img class="brand-mark" src="../assets/logo.png" alt="logo" />
          <h2 class="login-title">欢迎回来</h2>
          <p class="login-subtitle">请登录您的账号</p>
        </div>
        <el-form :model="loginForm" :rules="rules" ref="loginFormRef">
          <!-- 用户名输入 -->
          <el-form-item prop="username">
            <el-input
              v-model="loginForm.username"
              placeholder="请输入用户名"
              size="large"
              clearable
              autocomplete="username"
              @keyup.enter="focusPassword"
              ref="usernameInput"
            >
              <template #prefix>
                <el-icon>
                  <User />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>

          <!-- 密码输入 -->
          <el-form-item prop="password">
            <el-input
              v-model="loginForm.password"
              type="password"
              placeholder="请输入密码"
              size="large"
              show-password
              autocomplete="current-password"
              @keyup.enter="handleLogin"
              ref="passwordInput"
            >
              <template #prefix>
                <el-icon>
                  <Lock />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          
          <!-- 记住密码 -->
          <el-form-item class="remember-password">
            <el-checkbox v-model="loginForm.remember">记住用户</el-checkbox>
          </el-form-item>
          
          <!-- 错误提示 -->
          <el-form-item v-if="errorMsg">
            <el-alert 
              :title="errorMsg" 
              type="error" 
              show-icon 
              :closable="false"
              effect="dark"
            />
          </el-form-item>
          
          <!-- 登录按钮 -->
          <el-form-item>
            <el-button
              type="primary"
              @click="handleLogin"
              class="login-button"
              size="large"
              :loading="loading"
            >
              登录
            </el-button>
          </el-form-item>
        </el-form>

        <!-- 底部链接 -->
        <div class="login-footer">
          <a href="#" @click.prevent="handleForgotPassword" class="footer-link">忘记密码？</a>
          <a href="#" @click.prevent="handleReg" class="footer-link">注册账号</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus';
import request from '@/utils/request'; // 导入封装的 axios 实例
import { setPermissions } from '@/utils/permission';
import * as storage from '@/utils/storage';

const loginFormRef = ref(null);
const loading = ref(false);
const errorMsg = ref(null);
const isLogging = ref(false);
const usernameInput = ref(null);
const passwordInput = ref(null);

// 聚焦到密码输入框
const focusPassword = () => {
  passwordInput.value.focus();
};
const loginForm = ref({
  username: '',
  password: '',
  remember: false
});

// 表单验证规则
const rules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 2, max: 20, message: '用户名长度应在 2-20 个字符之间', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 3, message: '密码长度不能少于 3 个字符', trigger: 'blur' }
  ]
};

const router = useRouter();

// 提交表单
const handleLogin = () => {
  loginFormRef.value.validate((valid) => {
    if (valid) {
      doLogin();
    } else {
      ElMessage.error('请填写完整的登录信息');
    }
  });
};

// 登录请求
const doLogin = async() => {
  loading.value = true;
  errorMsg.value = null;
  isLogging.value = true;
  try {
    // 修改登录接口地址
    const response = await request.post('/base/user/login', loginForm.value);
    if (response.code === 0) {
      // 保存登录状态和凭证
      const data = response.data || {};
      storage.set('isLogin', 'true');
      storage.set('token', data.token);
      storage.set('refreshToken', data.refreshToken);
      storage.set('userInfo', JSON.stringify(data.userInfo));
      const permissions = data.permissions || (data.userInfo && data.userInfo.permissions) || [];
      setPermissions(permissions);
      
      // 如果记住密码，保存用户名
      if (loginForm.value.remember) {
        localStorage.setItem('rememberedUsername', loginForm.value.username);
      } else {
        localStorage.removeItem('rememberedUsername');
      }
      
      ElMessage.success('登录成功');
      // 延迟跳转，让背景图动画有时间完成
      setTimeout(() => {
        // 获取之前保存的跳转路径，默认为首页
        const redirectPath = localStorage.getItem('redirectPath') || '/home';
        // 清除保存的路径
        localStorage.removeItem('redirectPath');
        router.push(redirectPath);
      }, 500);
    } else {
      errorMsg.value = response.message || '登录失败';
      // 登录失败，恢复背景图
      setTimeout(() => {
        isLogging.value = false;
      }, 300);
    }
  } catch (err) {
    errorMsg.value = err.message || '网络错误，请稍后重试';
    // 登录失败，恢复背景图
    setTimeout(() => {
      isLogging.value = false;
    }, 300);
  } finally {
    loading.value = false;
  }
};

// 忘记密码
const handleForgotPassword = () => {
  ElMessage.info('请联系系统管理员重置密码');
};

// 注册账号
const handleReg = () => {
  ElMessage.warning('请联系系统管理员创建账号');
};

// 页面加载时，检查是否有记住的用户名
onMounted(() => {
  const rememberedUsername = localStorage.getItem('rememberedUsername');
  if (rememberedUsername) {
    loginForm.value.username = rememberedUsername;
    loginForm.value.remember = true;
  }
});
</script>

<style scoped>
/* 全局隐藏滚动条 */
* {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

*::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

/* 确保body和html没有滚动条 */
:global(body),
:global(html) {
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
  z-index: 2;
}

/* 背景图容器 */
.login-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(../assets/login_bg.jpg) no-repeat;
  background-size: cover;
  background-position: center;
  transition: transform 1.5s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 1;
}

/* 缓慢放大效果 - 只放大背景图 */
.login-bg.bg-zoom {
  transform: scale(1.1);
}

/* 背景遮罩 */
.login-bg::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.45), rgba(15, 23, 42, 0.2));
  backdrop-filter: saturate(120%);
  z-index: 1;
  transition: background 1.5s ease-in-out;
}

.login-bg.bg-zoom::before {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.25));
}

.login-right {
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.login-card {
  width: 100%;
  max-width: 420px;
  padding: 48px 44px 36px;
  border-radius: 20px;
  box-shadow: 0 20px 50px 0 rgba(15, 23, 42, 0.25);
  background-color: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.login-card:hover {
  box-shadow: 0 24px 60px 0 rgba(15, 23, 42, 0.3);
}

.login-card-animation {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 品牌区 */
.login-brand {
  text-align: center;
  margin-bottom: 32px;
}

.brand-mark {
  height: 64px;
  width: auto;
  margin: 0 auto;
  object-fit: contain;
  display: block;
}

.login-title {
  margin-bottom: 8px;
  font-size: 24px;
  color: #1f2937;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.login-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-weight: 400;
}

/* 输入框保持不透明白底，确保文字清晰可读 */
.login-card :deep(.el-input__wrapper) {
  background-color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8) inset;
  transition: box-shadow 0.2s ease, background-color 0.2s ease;
}

.login-card :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.5) inset;
}

.login-card :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset, 0 0 0 4px rgba(64, 158, 255, 0.12);
}

.remember-password {
  margin-bottom: 8px;
}

.login-card :deep(.el-checkbox__label) {
  color: #4b5563;
  font-size: 14px;
}

/* 输入框文字与占位符，提升可读性 */
.login-card :deep(.el-input__inner) {
  color: #1f2937;
  caret-color: #409eff;
}

.login-card :deep(.el-input__inner::placeholder) {
  color: #9ca3af;
}

.login-card :deep(.el-input__prefix) {
  color: #9ca3af;
}

.login-card :deep(.el-input__suffix .el-icon) {
  color: #6b7280;
}

/* 校验失败时的输入框状态 */
.login-card :deep(.el-form-item.is-error .el-input__wrapper) {
  box-shadow: 0 0 0 1px #f56c6c inset;
  background-color: rgba(255, 255, 255, 0.96);
}

.login-card :deep(.el-form-item.is-error .el-input__prefix) {
  color: #f56c6c;
}

/* 错误提示条柔和显示 */
.login-card :deep(.el-alert) {
  border-radius: 10px;
  animation: alertIn 0.4s ease-out;
}

@keyframes alertIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-button {
  width: 100%;
  margin-top: 12px;
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 2px;
  border-radius: 12px;
  background: linear-gradient(135deg, #409eff, #79bbff);
  border: none;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(64, 158, 255, 0.4);
  opacity: 0.95;
}

.login-button:active {
  transform: translateY(0);
}

.login-button.is-loading {
  transform: none;
  opacity: 0.85;
  cursor: not-allowed;
}

/* 键盘焦点可见性提示 */
.login-button:focus-visible,
.footer-link:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 3px;
  border-radius: 6px;
}

.login-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.5);
}

.footer-link {
  color: rgba(64, 158, 255, 0.9);
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: #409eff;
  text-decoration: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-right {
    padding: 16px;
  }

  .login-card {
    padding: 36px 26px 28px;
    box-shadow: 0 12px 32px 0 rgba(15, 23, 42, 0.2);
  }

  .login-brand {
    margin-bottom: 24px;
  }

  .login-title {
    font-size: 22px;
  }
}
</style>